import {
  useCreateSubGoalAllowed,
  useGoalControls,
  useGoals,
} from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React from 'react';
import { SelfSortingGoalSectionTaskList } from 'shared/components/ui/goal-section-task-list';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useHandleGoalSectionTaskChanges } from 'shared/hooks/use-handle-goal-section-task-changes';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';

import { useActionPlanItems } from '../../hooks/use-action-plan-items';
import * as Styled from './sub-items-list.style';

export type SubItemsListProps = {
  goal: Goal;
};

export const SubItemsList: React.FC<SubItemsListProps> = ({ goal }) => {
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();

  const openPremiumDialog = useOpenPremiumDialog();

  const { items } = useActionPlanItems();
  const { allowed: createSubGoalsAllowed } = useCreateSubGoalAllowed();
  const onReorder = useHandleGoalSectionTaskChanges(items);
  const { onCreateSubGoal, onUpdateGoal, onUpdateGoalProgress } =
    useGoalControls(goals, {
      onCreatePresets: { lifeAreaId: goal?.lifeAreaId },
    });

  const onUpdateSection = (sectionId: ID, sectionName: string) => {
    const goal = goals.find((goal) =>
      goal.sections?.find(({ id }) => id === sectionId),
    );
    if (!goal) {
      return;
    }

    const sections = goal.sections!.map((section) => ({
      ...section,
      name: section.id === sectionId ? sectionName : section.name,
    }));

    onUpdateGoal({ id: goal.id, sections });
  };

  const _onCreateSubGoal = () => {
    if (!goal) {
      return;
    }

    onCreateSubGoal([...(goal.parentIds ?? []), goal.id]);
  };

  return (
    <Styled.Container $noSpacing={!!goal.sections?.length}>
      <SelfSortingGoalSectionTaskList
        goalId={goal?.id ?? ''}
        allGoals={goals}
        lifeAreas={lifeAreas}
        onUpdateProgress={onUpdateGoalProgress}
        onPremium={openPremiumDialog}
        onUpdateSection={onUpdateSection}
        hideGoal
        items={items}
        onCreateSubGoal={
          createSubGoalsAllowed ? _onCreateSubGoal : openPremiumDialog
        }
        onReorder={onReorder}
        canCreateTask
        canCollapse
      />
    </Styled.Container>
  );
};
